import {useNudgePopover} from "./useNudgePopover";
import {useRevealIntent} from "./useRevealIntent";
import {UpgradeNudgeButton, UpgradeNudgeRoot, UpgradeNudgeTip} from "./RecipientNudgePopover.styles";

interface Props {
	/** The recipient-limit / upgrade copy revealed on hover / focus / tap. */
	message: string;
	/** Short visible label on the button — "Upgrade". */
	upgradeLabel: string;
	/**
	 * The button's accessible NAME, which says which tier it opens ("Upgrade to Basic"). Distinct from
	 * the visible label because the options sheet's sticky footer carries a second, plain "Upgrade"
	 * button: two controls sharing one accessible name gives a screen-reader user no way to tell the
	 * general route from this field's own offer. The visible label is a prefix of it, so the name still
	 * contains the label (WCAG 2.5.3).
	 */
	upgradeAria: string;
	/** Route to pricing; fired when the button itself is clicked. */
	onUpgrade: () => void;
	/** Id the button points at via `aria-describedby` while the tip is shown. */
	tipId: string;
}

/**
 * The recipient-cap upgrade nudge as a small right-aligned "Upgrade" button in the "Email to people"
 * section header (kitecorp/transferify-ui#2524). Shown only when the list has reached the plan's
 * recipients-per-transfer cap and a higher tier exists — {@link recipientUpgradeNudge} owns that rule,
 * this file only renders its answer.
 *
 * <b>History.</b> ui#213 put this button in the reveal header; ui#214 moved it into the options sheet's
 * sticky footer, where it shows for every tier below Premium whenever the sheet is open rather than
 * when the list is full, and this component was left unmounted. #2524 restores it beside the field it
 * is about, on the owner's 2026-09-17 decision, after #2518 removed the upsell card's recipients row.
 *
 * Hovering, focusing OR tapping the button reveals the offer in a small tooltip — the reveal is
 * deliberately not hover-only, so it is reachable by pointer, keyboard and touch alike (WCAG 1.4.13);
 * the button carries `aria-expanded` + `aria-describedby` to the tip. Clicking the button opens the
 * compact upgrade dialog on the target tier (ui#2529). The persistent visually-hidden `role="status"`
 * live region in the options sheet still carries the screen-reader announcement of the limit, so this
 * surface stays purely visual.
 *
 * Reuses {@link useRevealIntent} for the hover+focus intent and {@link useNudgePopover} for the
 * open/dismiss state — Escape dismisses the tip while the pointer/focus intent stays active. SSG-safe:
 * no document listeners, no browser globals touched at module scope.
 */
export function RecipientNudgePopover({message, upgradeLabel, upgradeAria, onUpgrade, tipId}: Props) {
	const reveal = useRevealIntent();
	const tip = useNudgePopover(reveal.active);

	return (
		<UpgradeNudgeRoot onMouseEnter={reveal.show} onMouseLeave={reveal.hide}>
			<UpgradeNudgeButton
				type="button"
				onClick={onUpgrade}
				onFocus={reveal.focus}
				onBlur={reveal.blur}
				onKeyDown={e => {
					if (e.key === "Escape") tip.dismiss();
				}}
				aria-label={upgradeAria}
				aria-expanded={tip.open}
				aria-describedby={tip.open ? tipId : undefined}
			>
				{upgradeLabel}
			</UpgradeNudgeButton>
			{tip.open && <UpgradeNudgeTip id={tipId} role="tooltip">{message}</UpgradeNudgeTip>}
		</UpgradeNudgeRoot>
	);
}
